import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import ApiClient from 'shared/lib/ApiClient';
import getImageSize from 'shared/lib/getImageSize';
import ProgressBar from 'shared/components/ProgressBar';
import { File } from 'shared/components/Form';
import DeleteButton from 'shared/components/buttons/Delete/Delete';
import { STATIC_BASE_URL } from 'shared/routes';
import styles from './Image.module.scss';

const Image = ({
  apiClient,
  uploadEndpoint,
  deleteEndpoint,
  currentImage,
  removePrevious,
  width,
  minHeight,
  maxHeight,
  error,
  className,
  onImageDimensionsCheck,
  onUploadSuccess,
  onUploadFailure,
  onUploadAbort,
  onDeleteSuccess,
  onDeleteFailure,
}) => {
  const fileRef = useRef();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [abortRequest, setAbortRequest] = useState({ callback: null });
  const { t } = useTranslation('common');

  const handleUploadSuccess = (response) => {
    fileRef.current.value = null;
    setUploadProgress(0);
    onUploadSuccess(response);
  };

  const handleUploadFailure = (err) => {
    fileRef.current.value = null;
    Sentry.captureException(err);
    setUploadProgress(0);
    setAbortRequest({ callback: null });
    onUploadFailure(err);
  };

  const setProgressCallback = ({ percent }) => setUploadProgress(Math.min(Math.ceil(percent), 100));

  const setAbortCallback = callback => setAbortRequest({ callback });

  const handleAbortUpload = () => {
    fileRef.current.value = null;
    if (abortRequest.callback) {
      abortRequest.callback();
    }
    setUploadProgress(0);
    onUploadAbort();
  };

  const handleOnSelect = async (event) => {
    event.persist();
    event.preventDefault();
    setUploadProgress(0);
    setAbortRequest({ callback: null });

    setTimeout(async () => {
      let files;
      if (event.dataTransfer) {
        files = event.dataTransfer.files;
      } else if (event.target) {
        files = event.target.files;
      }

      const [file] = files;
      if (!file) return;

      const size = await getImageSize(file);
      if (onImageDimensionsCheck && !onImageDimensionsCheck(size.width, size.height)) {
        fileRef.current.value = null;
        return;
      }

      try {
        const response = await apiClient.post(
          uploadEndpoint,
          removePrevious ? { replace: currentImage } : {},
          {}, // params
          [{ name: file.name, file }],
          setProgressCallback,
          setAbortCallback
        );
        handleUploadSuccess(response);
      } catch (err) {
        handleUploadFailure(err);
      }
    });
  };

  const handleDeleteImage = async () => {
    if (!window.confirm(t('image.confirmDelete'))) return;

    try {
      const response = await apiClient.delete(deleteEndpoint);
      onDeleteSuccess(response);
    } catch (err) {
      onDeleteFailure(err);
    }
  };

  return (
    <div className={cn(styles.image, className)} style={width || minHeight || maxHeight ? { width, minHeight, maxHeight } : undefined}>
      <div className={cn(styles.tools, { [styles.noImage]: !currentImage })}>
        <File
          ref={fileRef}
          error={error}
          loading={false}
          className={styles.file}
          accept="image/*"
          title={currentImage ? t('image.replace') : t('image.choose')}
          iconClassName="la la-file-upload"
          onSelect={handleOnSelect}
        />
        {currentImage && deleteEndpoint && uploadProgress === 0 && (
          <DeleteButton onClick={handleDeleteImage}>
            {t('image.delete')}
          </DeleteButton>
        )}
        {uploadProgress > 0 && (
          <ProgressBar
            withTitle
            value={uploadProgress}
            onAbort={handleAbortUpload}
            className={styles.progress}
          />
        )}
      </div>
      {currentImage && (
        <img src={`${STATIC_BASE_URL}${currentImage}`} alt="" />
      )}
    </div>
  );
};

Image.propTypes = {
  apiClient: PropTypes.instanceOf(ApiClient).isRequired,
  uploadEndpoint: PropTypes.string.isRequired,
  deleteEndpoint: PropTypes.string,
  currentImage: PropTypes.string,
  removePrevious: PropTypes.bool,
  width: PropTypes.number,
  minHeight: PropTypes.number,
  maxHeight: PropTypes.number,
  error: PropTypes.string,
  className: PropTypes.string,
  onImageDimensionsCheck: PropTypes.func,
  onUploadSuccess: PropTypes.func,
  onUploadFailure: PropTypes.func,
  onUploadAbort: PropTypes.func,
  onDeleteSuccess: PropTypes.func,
  onDeleteFailure: PropTypes.func,
};

Image.defaultProps = {
  deleteEndpoint: null,
  currentImage: null,
  removePrevious: false,
  width: null,
  minHeight: null,
  maxHeight: null,
  error: null,
  className: null,
  onImageDimensionsCheck: null,
  onUploadSuccess() {},
  onUploadFailure() {},
  onUploadAbort() {},
  onDeleteSuccess() {},
  onDeleteFailure() {},
};

export default Image;
